import { useState } from "preact/hooks"
import { Link } from "wouter-preact"
import { FileText, Loader2, Wand2 } from "lucide-react"

import { api_fetch_throws, TransactionJson } from "@/api"
import { Button } from "@/components/ui/button"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { useFetch } from "@/utils"
import { should_show_detail_spinner } from "@/utils/detail_refresh.ts"
import { CodingWizSheet } from "./CodingWizSheet.tsx"
import { SplitForm } from "./split_form.tsx"

interface OpenDeductionCodingPanelProps {
  deduction_id: string
  className?: string
}

/**
 * The accounting view's coding form, opened inline under an Open Deductions row so a parked
 * deduction can be split, recoded or sent to CodingWiz without leaving the queue. Saving refreshes
 * the queue through `RefreshFlag`, which re-checks whether the row is ready to sync.
 */
export function OpenDeductionCodingPanel({ deduction_id, className }: OpenDeductionCodingPanelProps) {
  const refresh_flag = RefreshFlag.use((flag: boolean) => flag)
  const [coding_wiz_open, setCodingWizOpen] = useState(false)
  const { data: transaction, loading } = useFetch(
    async () => api_fetch_throws<TransactionJson>(`/transaction/${deduction_id}`),
    [deduction_id, refresh_flag]
  )

  return (
    <div class={className}>
      <div class="flex items-center justify-end gap-2 pb-2">
        <Button variant="outline" size="sm" class="gap-1" onClick={() => setCodingWizOpen(true)}>
          <Wand2 className="h-4 w-4" />
          CodingWiz
        </Button>
        <Link href={`/deduction/${deduction_id}`}>
          <Button variant="outline" size="sm" class="gap-1">
            <FileText className="h-4 w-4" />
            Deduction View
          </Button>
        </Link>
      </div>
      {/* Keep the form mounted during background refetches so unsaved splits survive. */}
      {!transaction || should_show_detail_spinner(loading, transaction.id, deduction_id) ? (
        <div class="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading coding…
        </div>
      ) : (
        <SplitForm transaction={transaction} />
      )}
      <CodingWizSheet
        open={coding_wiz_open}
        onOpenChange={setCodingWizOpen}
        deductionId={deduction_id}
        onComplete={() => RefreshFlag.set((flag: boolean) => !flag)}
      />
    </div>
  )
}
